import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@chakra-ui/react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import type { ReportNoteTabKey } from "../../constants/reportNotes";
import type { Note } from "../../types/notes";
import { NotesListEditor } from "./NotesListEditor";
import { useNotesListControlled } from "./useNotesList";

const NOTES_ACCORDION_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]";

const NOTES_ACCORDION_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-xs font-normal text-gray-500 leading-5";

export function ReportTabNotesAccordion(props: {
  reportId: string;
  tabKeys: readonly string[];
  tabLabelFor: (tabKey: string) => string;
  tabNotes: Record<string, Note[]>;
  onTabNotesChange: (tabKey: string, notes: Note[]) => void;
  borderColor: string;
  subtleText: string;
  textColor: string;
}): React.ReactElement {
  const [expandedTabKey, setExpandedTabKey] = useState<string>("");

  useEffect(() => {
    setExpandedTabKey("");
  }, [props.reportId]);

  return (
    <Box data-report-tab-notes-accordion="" borderRadius="6px" overflow="hidden" w="100%">
      <Accordion
        type="single"
        collapsible
        value={expandedTabKey}
        onValueChange={(value) => setExpandedTabKey(value ?? "")}
        className="space-y-2 border-0"
      >
        {props.tabKeys.map((tabKey) => {
          const label = props.tabLabelFor(tabKey);
          const notes = props.tabNotes[tabKey] ?? [];
          const count = notes.length;
          const expanded = expandedTabKey === tabKey;
          const preview = notes[0]?.body?.trim() ?? "";

          return (
            <ReportTabNotesAccordionItem
              key={tabKey}
              tabKey={tabKey}
              reportId={props.reportId}
              label={label}
              notes={notes}
              count={count}
              expanded={expanded}
              preview={preview}
              borderColor={props.borderColor}
              subtleText={props.subtleText}
              textColor={props.textColor}
              onNotesChange={(next) => props.onTabNotesChange(tabKey, next)}
            />
          );
        })}
      </Accordion>
    </Box>
  );
}

function ReportTabNotesAccordionItem(props: {
  tabKey: string;
  reportId: string;
  label: string;
  notes: Note[];
  count: number;
  expanded: boolean;
  preview: string;
  borderColor: string;
  subtleText: string;
  textColor: string;
  onNotesChange: (notes: Note[]) => void;
}): React.ReactElement {
  const scope = useMemo(
    () => ({
      entityType: "report" as const,
      entityId: props.reportId,
      tabKey: props.tabKey as ReportNoteTabKey,
    }),
    [props.reportId, props.tabKey]
  );

  const [localNotes, setLocalNotes] = useState(props.notes);

  useEffect(() => {
    setLocalNotes(props.notes);
  }, [props.notes]);

  const syncNotes = useCallback(
    (updater: React.SetStateAction<Note[]>) => {
      setLocalNotes((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        props.onNotesChange(next);
        return next;
      });
    },
    [props]
  );

  const editor = useNotesListControlled(localNotes, syncNotes, scope);

  return (
    <AccordionItem
      value={props.tabKey}
      data-report-tab-notes-item=""
      className="border-border rounded-lg border px-3 not-last:border-b"
    >
      <AccordionTrigger
        className="items-start py-3 font-medium hover:no-underline"
        aria-label={
          props.expanded
            ? `Collapse notes: ${props.label}, ${props.count} note${props.count === 1 ? "" : "s"}`
            : `Expand notes: ${props.label}, ${props.count} note${props.count === 1 ? "" : "s"}`
        }
      >
        <div className="flex w-full min-w-0 flex-col gap-1 pr-2 text-left">
          <Field className="gap-1">
            <FieldLabel className={`${NOTES_ACCORDION_LABEL_CLASS} inline-flex flex-wrap items-center gap-x-2 gap-y-0`}>
              <span className="min-w-0">{props.label}</span>
              <span className="text-xs font-medium text-gray-500" aria-hidden>
                ({props.count})
              </span>
            </FieldLabel>
            {!props.expanded && props.preview ? (
              <FieldDescription className={`${NOTES_ACCORDION_DESC_CLASS} line-clamp-3`}>
                {props.preview}
              </FieldDescription>
            ) : !props.expanded && props.count === 0 ? (
              <FieldDescription className={NOTES_ACCORDION_DESC_CLASS}>
                No notes for this section yet.
              </FieldDescription>
            ) : null}
          </Field>
        </div>
      </AccordionTrigger>
      <AccordionContent className="text-muted-foreground pt-0 pb-4">
        <Box
          id={`report-tab-notes-panel-${props.tabKey}`}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <NotesListEditor
            composerVariant="shadcn"
            notes={editor.notes}
            draft={editor.draft}
            onDraftChange={editor.setDraft}
            editingId={editor.editingId}
            editingBody={editor.editingBody}
            onEditingBodyChange={editor.setEditingBody}
            saving={editor.saving}
            canSubmit={editor.canSubmit}
            onCreate={() => void editor.onCreate()}
            onStartEdit={editor.startEdit}
            onCancelEdit={editor.cancelEdit}
            onSaveEdit={() => void editor.saveEdit()}
            onDelete={(id) => void editor.onDelete(id)}
            borderColor={props.borderColor}
            subtleText={props.subtleText}
            textColor={props.textColor}
            emptyLabel="No notes for this section yet."
          />
        </Box>
      </AccordionContent>
    </AccordionItem>
  );
}
